"use client";

import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";
import { SITE_CONTROL_HERO_SIGNPOST } from "@/lib/site-control-copy";

/**
 * Site Control — hero discoverability signpost for the /pricing page.
 *
 * Self-gating on the DB-backed `site_control` flag (read from the pricing
 * page's AppFeatureFlagsProvider), now a GA kill-switch (fail-open server-side):
 * live unless an operator explicitly disables the tier, hidden only when killed. It
 * sits just below the orange "Pro is £39/month" pill and smooth-jumps down to
 * the Site Control card lower on the page (id="site-control", added on the
 * pricing card). It is a plain in-page hash anchor (NOT next/link), consistent
 * with the existing `<a href="#pro">` pill — smooth scrolling + the card's
 * scroll-mt-24 offset are provided globally, so no inline JS is needed.
 *
 * Styled lighter than the orange Pro pill (navy hairline + surface fill) so it
 * echoes the enterprise card without competing with the primary CTA. Copy is
 * sourced from lib/site-control-copy.ts.
 */
export function SiteControlHeroSignpost() {
  const flagEnabled = useAppFeatureFlag("site_control");

  // Off (or undefined) → render nothing.
  if (flagEnabled !== true) return null;

  return (
    <div className="mt-3">
      <a
        href="#site-control"
        data-testid="site-control-hero-signpost"
        className="inline-flex items-center gap-2 rounded-2xl border border-navy/30 bg-surface px-4 py-2 text-sm font-semibold text-brand-700 transition-colors hover:border-navy hover:text-accent-dark sm:rounded-full"
      >
        <span>{SITE_CONTROL_HERO_SIGNPOST}</span>
        <span aria-hidden>&rarr;</span>
      </a>
    </div>
  );
}
